import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, m as motion } from 'framer-motion';
import { Bell, CalendarClock, ClipboardList, LifeBuoy, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useSession } from '@/stores/session';
import { gatherNotifications, type AppNotification, type NotificationKind } from '@/services/notifications';

const ICONS: Record<NotificationKind, typeof Bell> = {
  task_overdue: ListTodo,
  appointment_today: CalendarClock,
  ticket_open: LifeBuoy,
  request_pending: ClipboardList,
};

/** Campana con centro de notificaciones REAL (VLT-361): avisos derivados de
 *  datos vivos (tareas vencidas, citas de hoy, tickets, requisitos del
 *  cliente); el punto solo aparece si HAY algo, y cada aviso navega a donde
 *  se resuelve. */
export function NotificationsMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const refresh = () => void gatherNotifications(role).then(setItems);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = items?.length ?? 0;

  return (
    <div ref={rootRef} className="relative">
      <Button
        variant="glass"
        size="sm"
        aria-label={t('notifications.title')}
        aria-expanded={open}
        className="relative"
        onClick={() => { setOpen((v) => !v); if (!open) refresh(); }}
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute right-1 top-1 grid h-3.5 min-w-3.5 place-items-center rounded-full bg-gold px-0.5 text-[9px] font-bold text-black">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </Button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            role="menu"
            aria-label={t('notifications.title')}
            className="glass absolute right-0 top-full z-40 mt-2 w-80 rounded-2xl border border-border/60 p-2 shadow-xl"
          >
            <p className="px-2.5 pb-1.5 pt-1 font-display text-xs font-semibold text-muted-foreground">
              {t('notifications.title')}{count > 0 ? ` · ${count}` : ''}
            </p>
            {items === null ? (
              <div className="skeleton mx-2 mb-2 h-16" aria-busy="true" />
            ) : items.length === 0 ? (
              <p className="px-2.5 pb-2 text-sm text-muted-foreground">{t('notifications.empty')}</p>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {items.map((n) => {
                  const Icon = ICONS[n.kind];
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setOpen(false); navigate(n.to); }}
                        className="focus-ring flex w-full items-start gap-2.5 rounded-xl px-2.5 py-2 text-left hover:bg-muted/40"
                      >
                        <Icon className="mt-0.5 h-4 w-4 flex-none text-primary" aria-hidden />
                        <span className="min-w-0 text-sm leading-snug">{t(`notifications.items.${n.kind}`, n.params)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
